import { useEffect, useState } from "react";

const COUNT_UP_DURATION_MS = 360;

/**
 * Animates a number from 0 to `target` with an ease-out curve after `delay`
 * ms. Jumps straight to the target when the user prefers reduced motion.
 */
export function useCountUp(target: number, delay = 0): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setValue(target);
      return;
    }
    let frame: number;
    let startTime: number | null = null;

    const tick = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime - delay;
      if (elapsed < 0) { frame = requestAnimationFrame(tick); return; }
      const t = Math.min(elapsed / COUNT_UP_DURATION_MS, 1);
      const eased = 1 - Math.pow(2, -10 * t);
      setValue(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
      else setValue(target);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, delay]);

  return value;
}
